import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MarketIndices } from '../components/market/MarketIndices';
import { UserPortfolioChart } from '../components/charts/UserPortfolioChart';
import { MarketNews } from '../components/news/MarketNews';
import { PortfolioManagerEnhanced } from '../components/portfolio/PortfolioManagerEnhanced';
import { WatchlistManagerEnhanced } from '../components/portfolio/WatchlistManagerEnhanced';
import { TabContainer } from '../components/ui/TabContainer';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { HeaderSearchBar } from '../components/ui';
import { useToast } from '../hooks/useToast';
import { PortfolioService } from '../services/PortfolioService';
import type { PortfolioWithQuotes } from '../services/PortfolioService';
import {
  RefreshCw, LogOut, X, TrendingUp, BarChart3,
  Newspaper, List, PlusCircle, Eye
} from 'lucide-react';

export const Dashboard: React.FC = () => {
  const { user, signOut, loading } = useAuth();
  const [portfolios, setPortfolios] = useState<PortfolioWithQuotes[]>([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(true);
  const [showMoreNews, setShowMoreNews] = useState(false);
  const [showPortfolioManager, setShowPortfolioManager] = useState(false);
  const [showWatchlistManager, setShowWatchlistManager] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { showToast } = useToast();

  useEffect(() => {
    if (user?.id) loadUserPortfolios();
  }, [user?.id]);

  useEffect(() => {
    const iv = setInterval(() => setRefreshKey(p => p + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const loadUserPortfolios = async () => {
    try {
      setPortfoliosLoading(true);
      if (user?.id) {
        const p = await PortfolioService.getUserPortfolios(user.id, true);
        setPortfolios(p);
      }
    } catch { /* silent */ }
    finally { setPortfoliosLoading(false); }
  };

  const handleRefresh = () => {
    setRefreshKey(p => p + 1);
    showToast('Market data refreshed', 'success');
  };

  /* ── Icon SVGs for tab icons ── */
  const watchlistIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
  const portfolioIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );

  return (
    <ErrorBoundary>
      <div className="page-container">
        <a href="#main-content" className="skip-to-content">Skip to main content</a>

        {/* ── Top header ── */}
        <header style={{
          background: 'rgba(10,14,26,0.92)',
          borderBottom: '1px solid #1e2d45',
          position: 'sticky', top: 0, zIndex: 50,
          backdropFilter: 'blur(16px)',
        }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 1rem' }}>
            {/* Mobile */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.625rem 0' }}
              className="lg:hidden">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BarChart3 size={14} color="#fff" strokeWidth={2.5} />
                  </div>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 800, letterSpacing: '-0.03em', color: '#f1f5f9' }}>
                    Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <button onClick={handleRefresh} className="btn-icon" title="Refresh" aria-label="Refresh">
                    <RefreshCw size={14} strokeWidth={2} />
                  </button>
                  <button
                    onClick={signOut}
                    disabled={loading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '0.375rem 0.625rem', borderRadius: 7,
                      background: 'transparent', border: '1px solid #1e2d45',
                      color: '#64748b', fontSize: '0.75rem', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.18s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <LogOut size={12} strokeWidth={2} />
                    Sign Out
                  </button>
                </div>
              </div>
              <HeaderSearchBar className="w-full" />
            </div>

            {/* Desktop */}
            <div className="hidden lg:flex" style={{ display: 'none', justifyContent: 'space-between', alignItems: 'center', height: 60 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 12px rgba(59,130,246,0.3)',
                }}>
                  <BarChart3 size={16} color="#fff" strokeWidth={2.5} />
                </div>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, letterSpacing: '-0.03em', color: '#f1f5f9', lineHeight: 1.2 }}>
                    Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#334155', fontWeight: 500 }}>
                    Welcome, {user?.email?.split('@')[0]}
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, maxWidth: 420, margin: '0 2rem' }}>
                <HeaderSearchBar />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <button onClick={handleRefresh} className="btn-icon" title="Refresh" aria-label="Refresh market data">
                  <RefreshCw size={15} strokeWidth={2} />
                </button>
                <button
                  onClick={signOut}
                  disabled={loading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                    padding: '0.4375rem 0.875rem', borderRadius: 8,
                    background: 'transparent', border: '1px solid #1e2d45',
                    color: '#64748b', fontSize: '0.8125rem', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.18s', letterSpacing: '-0.01em',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <LogOut size={14} strokeWidth={2} />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </header>

        <main id="main-content" style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* ── Market Overview ── */}
          <section aria-label="Market indices">
            <div style={{
              background: '#111827',
              border: '1px solid #1e2d45',
              borderRadius: 14,
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.75rem 1.25rem',
                borderBottom: '1px solid #1e2d45',
                background: '#0f1621',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={15} color="#3b82f6" strokeWidth={2.5} />
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Market Overview
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div className="live-dot" />
                  <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 500 }}>Live</span>
                </div>
              </div>
              <MarketIndices key={`indices-${refreshKey}`} />
            </div>
          </section>

          {/* ── Watchlists & Portfolios ── */}
          {user && (
            <section aria-label="Portfolio and watchlist management">
              <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
                <TabContainer
                  tabs={[
                    {
                      id: 'watchlists',
                      label: 'Watchlists',
                      icon: watchlistIcon,
                      content: (
                        <div style={{ padding: '1.25rem' }}>
                          <WatchlistManagerEnhanced userId={user.id} />
                        </div>
                      ),
                    },
                    {
                      id: 'portfolios',
                      label: 'Portfolios',
                      icon: portfolioIcon,
                      badge: portfolios.length,
                      content: (
                        <div style={{ padding: '1.25rem' }}>
                          <PortfolioManagerEnhanced userId={user.id} />
                        </div>
                      ),
                    },
                  ]}
                  defaultActiveTab="watchlists"
                />
              </div>
            </section>
          )}

          {/* ── Portfolio Performance ── */}
          <section aria-label="Portfolio performance">
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                borderBottom: '1px solid #1e2d45',
                background: '#0f1621',
              }}>
                <BarChart3 size={15} color="#8b5cf6" strokeWidth={2.5} />
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Portfolio Performance
                </span>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <UserPortfolioChart portfolios={portfolios} loading={portfoliosLoading} />
              </div>
            </div>
          </section>

          {/* ── Market News ── */}
          <section aria-label="Market news">
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.75rem 1.25rem',
                borderBottom: '1px solid #1e2d45',
                background: '#0f1621',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Newspaper size={15} color="#10b981" strokeWidth={2.5} />
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Market News
                  </span>
                </div>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#334155', background: '#1a2235', padding: '0.1875rem 0.5rem', borderRadius: 6 }}>
                  Top {showMoreNews ? 20 : 10}
                </span>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <MarketNews
                  key={`news-${refreshKey}`}
                  limit={showMoreNews ? 20 : 10}
                  showFilters={showMoreNews}
                  compact={!showMoreNews}
                  autoRefresh={true}
                />
              </div>
              <div style={{
                borderTop: '1px solid #1a2235',
                padding: '0.75rem 1.25rem',
                textAlign: 'center',
                background: '#0f1621',
              }}>
                <button
                  onClick={() => setShowMoreNews(!showMoreNews)}
                  style={{
                    background: 'none', border: 'none',
                    fontSize: '0.8125rem', fontWeight: 600,
                    color: '#3b82f6', cursor: 'pointer',
                    padding: '0.25rem 0.5rem', borderRadius: 6,
                    transition: 'color 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#3b82f6'; }}
                >
                  {showMoreNews ? 'Show less' : 'Show more news'}
                </button>
              </div>
            </div>
          </section>

          {/* ── Empty state ── */}
          {portfolios.length === 0 && !portfoliosLoading && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(139,92,246,0.05))',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: 14,
              padding: '2.5rem',
              textAlign: 'center',
            }}>
              <div style={{
                width: 56, height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem',
                boxShadow: '0 8px 24px rgba(59,130,246,0.25)',
              }}>
                <TrendingUp size={24} color="#fff" strokeWidth={2} />
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
                Start Your Investment Journey
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1.5rem', maxWidth: 400, margin: '0 auto 1.5rem', lineHeight: 1.65 }}>
                Create your first portfolio or watchlist to track investments and get AI-powered insights.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPortfolioManager(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#3b82f6', color: '#fff',
                    padding: '0.625rem 1.25rem', borderRadius: 9,
                    fontSize: '0.875rem', fontWeight: 600,
                    border: 'none', cursor: 'pointer',
                    transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <PlusCircle size={15} strokeWidth={2} />
                  Create Portfolio
                </button>
                <button
                  onClick={() => setShowWatchlistManager(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                    padding: '0.625rem 1.25rem', borderRadius: 9,
                    fontSize: '0.875rem', fontWeight: 600,
                    border: '1px solid rgba(139,92,246,0.25)', cursor: 'pointer',
                    transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.25)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; }}
                >
                  <Eye size={15} strokeWidth={2} />
                  Create Watchlist
                </button>
              </div>
            </div>
          )}
        </main>

        {/* ── Portfolio Modal ── */}
        {showPortfolioManager && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
            role="dialog" aria-modal="true" aria-label="Portfolio Manager"
          >
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 16, maxWidth: '95vw', width: 1100, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: '1px solid #1e2d45', background: '#0f1621', flexShrink: 0 }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.02em' }}>Portfolio Manager</h2>
                <button
                  onClick={() => setShowPortfolioManager(false)}
                  className="btn-icon" aria-label="Close"
                >
                  <X size={16} strokeWidth={2} />
                </button>
              </div>
              <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                {user?.id && <PortfolioManagerEnhanced userId={user.id} />}
              </div>
            </div>
          </div>
        )}

        {/* ── Watchlist Modal ── */}
        {showWatchlistManager && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
            role="dialog" aria-modal="true" aria-label="Watchlist Manager"
          >
            <div style={{ background: '#111827', border: '1px solid #1e2d45', borderRadius: 16, maxWidth: '95vw', width: 1100, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: '1px solid #1e2d45', background: '#0f1621', flexShrink: 0 }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.02em' }}>Watchlist Manager</h2>
                <button
                  onClick={() => setShowWatchlistManager(false)}
                  className="btn-icon" aria-label="Close"
                >
                  <X size={16} strokeWidth={2} />
                </button>
              </div>
              <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                {user?.id && <WatchlistManagerEnhanced userId={user.id} />}
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
